import React from 'react';
import QRCode from 'react-qr-code';

import { ActionIcon } from '@cowbell/ui';

import { Show } from '../../Show';
import { rem } from './mfa.styles';
import { QR_SVG_SIZE } from './qrCodeImage.utils';

const COPY_LABEL = 'Copy QR code to clipboard';

export interface MfaQrCodeProps {
  readonly qrValue: string;
  readonly svgHostRef: React.RefObject<HTMLDivElement>;
  readonly pngDataUrl: string | null;
  readonly isCopying: boolean;
  readonly onCopy: () => void;
}

/**
 * The QR code a user scans to enrol an authenticator app, plus the control that
 * copies it.
 *
 * Two renderings of the same code live here on purpose. The `<svg>` is what
 * `react-qr-code` produces and the source the PNG is traced from; the `<img>`
 * is what gets shown once that PNG exists, because "Copy image" on the context
 * menu is something browsers only offer on an `<img>`. Until the PNG is ready —
 * or if it never arrives — the `<svg>` stays visible and the code is still
 * scannable.
 */
export const MfaQrCode: React.FC<MfaQrCodeProps> = ({
  qrValue,
  svgHostRef,
  pngDataUrl,
  isCopying,
  onCopy,
}) => {
  const hasPng = pngDataUrl !== null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: rem(8),
      }}
    >
      <div ref={svgHostRef} style={{ display: hasPng ? 'none' : 'block' }}>
        <QRCode
          value={qrValue}
          size={QR_SVG_SIZE}
          style={{
            display: 'block',
            width: rem(QR_SVG_SIZE),
            height: rem(QR_SVG_SIZE),
          }}
        />
      </div>

      <Show when={hasPng}>
        <img
          src={pngDataUrl ?? undefined}
          alt="MFA setup QR code"
          style={{
            display: 'block',
            width: rem(QR_SVG_SIZE),
            height: rem(QR_SVG_SIZE),
          }}
        />
      </Show>

      <ActionIcon
        variant="default"
        color="dark"
        size="lg"
        title={COPY_LABEL}
        aria-label={COPY_LABEL}
        loading={isCopying}
        disabled={!hasPng}
        onClick={onCopy}
      >
        <CopyGlyph />
      </ActionIcon>
    </div>
  );
};

const CopyGlyph: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    style={{ width: rem(18), height: rem(18) }}
  >
    <path
      fill="currentColor"
      d="M16 1H4a2 2 0 0 0-2 2v14h2V3h12V1Zm3 4H8a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 16H8V7h11v14Z"
    />
  </svg>
);
